import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import Button from "../../../components/shared/buttons/button";
import toast from "react-hot-toast";
import useTranslator from "../../../hooks/useTranslator";

const SeparationModal = ({ 
  show, 
  setShow, 
  record, 
  mode, 
  onSave, 
  date, 
  lotNumber,
  shift, // "day" or "night"
  separationArea // "Bagging" or "Sorting"
}) => {
  const { _t } = useTranslator();

  const formatSeparationAreaLabel = (area) =>
    (area || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const separationAreaOptions = [
    { value: "bagging", label: _t("bagging", "Bagging") },
    {
      value: "Packing_and_Sorting",
      label: _t("packing_and_sorting", "Packing and Sorting"),
    },
  ];

  const hasCurrentSeparationArea = separationAreaOptions.some(
    (option) => option.value === (separationArea || "bagging")
  );

  const separationAreaDisplayOptions = hasCurrentSeparationArea
    ? separationAreaOptions
    : [
        ...separationAreaOptions,
        {
          value: separationArea,
          label: formatSeparationAreaLabel(separationArea),
        },
      ];

  const initialFormData = {
    lot_number: lotNumber || "",
    strips_kg: "",
    cheeks_kg: "",
    shift: shift || "day",
    total_people: "",
    separation_area: separationArea || "bagging",
    date: date || "",
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (show) {
      if (record && mode === "edit") {
        setFormData({
          lot_number: record.lot_number || lotNumber || "",
          strips_kg: record.strips_kg || "",
          cheeks_kg: record.cheeks_kg || "",
          shift: shift || "day",
          total_people: record.total_people || "",
          separation_area: separationArea || "bagging",
          date: date || "",
        });
      } else {
        setFormData({
          ...initialFormData,
          lot_number: lotNumber || "",
          date: date || "",
          shift: shift || "day",
          separation_area: separationArea || "bagging",
        });
      }
    }
  }, [show, record, mode, date, lotNumber, shift, separationArea]);

  const handleClose = () => {
    setShow(false);
    setFormData(initialFormData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const requiredFields = [
      {
        key: "strips_kg",
        label: _t("strips_kg", "Strips KG"),
      },
      {
        key: "cheeks_kg",
        label: _t("cheeks_kg", "Cheeks KG"),
      },
      {
        key: "total_people",
        label: _t("number_of_people_separating", "Number of people Separating"),
      },
      {
        key: "separation_area",
        label: _t("separation_area", "Separation Area"),
      },
    ];

    const emptyField = requiredFields.find(({ key }) => {
      const value = formData[key];
      return value === "" || value === null || value === undefined;
    });

    if (emptyField) {
      return toast.error(
        _t(
          "all_separation_values_required",
          `Please enter :field before submitting.`,
          {field : emptyField.label}
        )
      );
    }

    if (onSave) {
      onSave(formData);
    }
    handleClose();
  };

  const shiftLabel = shift === "day" ? _t("day_separation", "Day Separation") : _t("night_separation", "Shift 2 Seperating");

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        mode === "edit"
          ? `${_t("edit", "Edit")} ${shiftLabel} `
          : `${_t("add", "Add")} ${shiftLabel}`
      }
      children={formData}
    >
      <Row className="mb-1">
        <Col lg={6}>
          <InputWithLabel
            label={_t("lot_number", "Lot Number")}
            type="text"
            name="lot_number"
            value={formData.lot_number}
            onChange={handleChange}
            placeholder="Lot Number"
            disabled={true}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t("shift", "Shift")}
            type="text"
            name="shift"
            value={shiftLabel}
            onChange={handleChange}
            disabled={true}
          />
        </Col>
      </Row>

      <Row className="mb-1">
        <Col lg={6}>
          <InputWithLabel
            label={_t("strips_kg", "Strips KG")}
            type="number"
            name="strips_kg"
            value={formData.strips_kg}
            onChange={handleChange}
            placeholder={"0"}
            required={true}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t("cheeks_kg", "Cheeks KG")}
            type="number"
            name="cheeks_kg"
            value={formData.cheeks_kg}
            onChange={handleChange}
            placeholder={"0"}
            required={true}
          />
        </Col>
      </Row>

      <Row className="mb-1">
        <Col lg={6}>
          <InputWithLabel
            label={_t("number_of_people_separating", "Number of people Separating")}
            type="number"
            name="total_people"
            value={formData.total_people}
            onChange={handleChange}
            placeholder={"0"}
            required={true}
          />
        </Col>
        <Col lg={6}>
          <SelectBox
            label={_t("separation_area", "Separation Area")}
            name="separation_area"
            value={formData.separation_area}
            onChange={handleChange}
            options={separationAreaDisplayOptions}
            disabled={true}
            required={true}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col className="text-end">
          <Button
            label={_t("save_changes", "Save Changes")}
            size="small"
            onClick={handleSubmit}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default SeparationModal;
